import Link from "next/link";
import { useTranslation } from "react-i18next";

import LazyImage from "@/my_components/Common/LazyImage";
import { useProfile } from "@/context/ProfileContext";
import { ROUTE } from "@/route/app_routes.js";

const HIGHLIGHTS = [
  { icon: "feather-calendar", key: "post", text: "Post in minutes" },
  { icon: "feather-tag", key: "tickets", text: "Free ticketing" },
  { icon: "feather-bar-chart-2", key: "stats", text: "Live stats" },
];

/**
 * "You could be hosting this" -- a visible, image-led promo inviting whoever's looking at an
 * events listing or an event's details to post their own, public or private, and manage tickets
 * for free. Routes the CTA to Create Event when the visitor is already signed in, or Register
 * when they're not (so a logged-out visitor isn't bounced into a create-event form it can't
 * actually submit). Shown on both the public events surfaces AND the student dashboard's own
 * events pages -- an already-registered student is still a prospective organizer.
 *
 * `bare` skips the outer <section>/.container wrapper for callers that already provide their
 * own container (the dashboard's DashboardShell already wraps its children in one -- nesting a
 * second .container inside that would double up the max-width/padding and visibly shrink the
 * card instead of letting it use the available column width).
 *
 * `compact` renders the tighter, horizontal-strip layout meant to sit INSIDE an event-card grid
 * as its own full-width row (see sections/Events.js) -- shorter heading, one-line subtitle, no
 * bullet list, smaller media -- instead of the larger standalone card used everywhere else.
 */
const HostEventPromo = ({ bare = false, compact = false }) => {
  const { t } = useTranslation("events");
  const { loggedIn } = useProfile();
  const ctaHref = loggedIn ? ROUTE.dashboardEventsCreate : ROUTE.register;

  const card = (
    <div className={`rbt-host-promo__card${compact ? " rbt-host-promo__card--compact" : ""}`}>
      <div className="rbt-host-promo__media">
        <LazyImage
          src="/app_images/event/host-your-event-v2.webp"
          alt={t("details.hostPromo.imageAlt", "A packed, premium conference audience")}
          fill
          sizes={compact ? "(max-width: 767px) 100vw, 320px" : "(max-width: 991px) 100vw, 46vw"}
        />
      </div>

      <div className="rbt-host-promo__content">
        <div className="rbt-host-promo__text">
          <span className="rbt-host-promo__eyebrow">{t("details.hostPromo.eyebrow", "For organizers")}</span>
          <h2>{t("details.hostPromo.title", "Host your own event — free")}</h2>
          <p>
            {t(
              "details.hostPromo.subtitle",
              "Publish events, manage tickets, and track attendees — at no cost."
            )}
          </p>

          {!compact && (
            <ul className="rbt-host-promo__list">
              {HIGHLIGHTS.map((h) => (
                <li key={h.key}>
                  <span className="rbt-host-promo__list-icon"><i className={h.icon}></i></span>
                  {t(`details.hostPromo.${h.key}`, h.text)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Link className="rbt-host-promo__cta" href={ctaHref}>
          <span>{t("details.hostPromo.cta", "Create your event")}</span>
          <i className="feather-arrow-right"></i>
        </Link>
      </div>
    </div>
  );

  if (bare) return card;

  return (
    <section className={`rbt-host-promo${compact ? " rbt-host-promo--compact" : ""}`}>
      <div className="container">{card}</div>
    </section>
  );
};

export default HostEventPromo;
